import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import moment from 'moment';
import AdvanceTableResponsive from '../../../components/common/advance-table/AdvanceTableResponsive';
import CardDropdown from '../../../components/common/CardDropdown';
import PendingListLoadingState from './common/PendingListLoadingState';
import useAdvanceTable from '../../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../../providers/AdvanceTableProvider';
import { useOrderContext } from '../../../providers/OrderProvider';
import { socket } from '../../../socket';
import { TAIL_QUASI_RECEIVABLE_LABEL } from '../../../utils/order/orderAccountsReceivable';
import OrderDeliveryStatusCell from './common/OrderDeliveryStatusCell';
import PendingListStatusBadge from './common/PendingListStatusBadge';
import PendingListEmptyState from './common/PendingListEmptyState';
import { PendingKeywordHighlight, PendingTotalPriceHighlight, PendingAmountHighlight } from './common/PendingKeywordHighlight';
import PendingListTableFooter from './common/PendingListTableFooter';
import PendingListToolbar from './common/PendingListToolbar';
import PendingListStatsPanel from './common/PendingListStatsPanel';
import PendingListStatsOverlay from './common/PendingListStatsOverlay';
import PendingOrderIdCell from './common/PendingOrderIdCell';
import { PENDING_TABLE_PER_PAGE, PENDING_TAB_BADGE } from './common/pendingTableConfig';
import { buildPendingActivityAtColumn, PENDING_ACTIVITY_AT_SORT } from './common/pendingActivityAtColumn';
import { usePendingKeywordFilter } from './common/usePendingKeywordFilter';
import AcceptModal from './modal/AcceptModal';

const AcceptingOrderPendings = () => {
	const [isOpenPendingModal, setIsOpenPendingModal] = useState(false);
	const {
		acceptingOrders = [],
		acceptAccountsReceivable = 0,
		acceptTotalQuasiReceivable = 0,
		acceptTotalPriceSum = 0,
		isLoadingAcceptingOrder,
		setCurrentOrderOIdState,
	} = useOrderContext();

	const [acceptPendingStat, setAcceptPendingStat] = useState('total'); // total | accountsReceivable | quasiReceivable

	useEffect(() => {
		if (acceptPendingStat === 'quasiReceivable' && Number(acceptTotalQuasiReceivable) <= 0) {
			setAcceptPendingStat('total');
		}
	}, [acceptPendingStat, acceptTotalQuasiReceivable]);

	const handleOrderClick = useCallback(
		(orderId) => {
			setIsOpenPendingModal(true);
			setCurrentOrderOIdState(orderId);
			socket.emit('join order', orderId);
		},
		[setCurrentOrderOIdState],
	);

	const formatDateTime = useCallback((date) => {
		return moment(date).format('YYYY-MM-DD HH:mm');
	}, []);

	const handleAcceptStatClick = useCallback((statKey) => {
		setAcceptPendingStat(statKey);
	}, []);

	const statsItems = useMemo(() => {
		const source = Array.isArray(acceptingOrders) ? acceptingOrders : [];
		const items = [];
		if (Number(acceptTotalPriceSum) > 0) {
			items.push({
				key: 'total',
				label: '总计',
				amount: Number(acceptTotalPriceSum),
				count: source.length,
				active: acceptPendingStat === 'total',
			});
		}
		if (Number(acceptAccountsReceivable) > 0) {
			items.push({
				key: 'accountsReceivable',
				label: '应收账款',
				amount: Number(acceptAccountsReceivable),
				count: source.filter((order) => Number(order?.currentAccountsReceivable) > 0).length,
				active: acceptPendingStat === 'accountsReceivable',
			});
		}
		if (Number(acceptTotalQuasiReceivable) > 0) {
			items.push({
				key: 'quasiReceivable',
				label: TAIL_QUASI_RECEIVABLE_LABEL,
				amount: Number(acceptTotalQuasiReceivable),
				count: source.filter((order) => Number(order?.quasiReceivable) > 0).length,
				active: acceptPendingStat === 'quasiReceivable',
			});
		}
		return items;
	}, [acceptingOrders, acceptTotalPriceSum, acceptAccountsReceivable, acceptTotalQuasiReceivable, acceptPendingStat]);

	const columns = useMemo(() => {
		const receiptColumns = [];

		if (acceptPendingStat === 'accountsReceivable') {
			receiptColumns.push({
				accessorKey: 'currentAccountsReceivable',
				header: '应收账款',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-end' },
					cellProps: { className: 'text-end' },
				},
				cell: ({ row: { original } }) => {
					const { currentAccountsReceivable } = original;
					return (
						<PendingListStatusBadge
							bg={PENDING_TAB_BADGE.accountsReceivable}
							text={<PendingAmountHighlight amount={currentAccountsReceivable} />}
							pill
							showIcon={false}
						/>
					);
				},
			});
		}

		if (acceptPendingStat === 'quasiReceivable') {
			receiptColumns.push({
				accessorKey: 'quasiReceivable',
				header: TAIL_QUASI_RECEIVABLE_LABEL,
				meta: {
					headerProps: { className: 'pe-3 text-900 text-end' },
					cellProps: { className: 'text-end' },
				},
				cell: ({ row: { original } }) => {
					const quasiAmount = Number(original.quasiReceivable) || 0;
					return (
						<PendingListStatusBadge
							bg='success'
							text={<PendingAmountHighlight amount={quasiAmount} />}
							pill
							showIcon={false}
						/>
					);
				},
			});
		}

		return [
			{
				accessorKey: 'orderId',
				header: '订单编号',
				meta: {
					headerProps: { className: 'sticky-column text-900 text-start' },
					cellProps: {
						className: 'fw-semibold text-start',
					},
				},
				cell: ({ row: { original } }) => {
					const { orderId, _id } = original;
					return (
						<PendingOrderIdCell
							orderId={orderId}
							orderMongoId={_id}
							badgeVariant={PENDING_TAB_BADGE.accepting}
							onOrderClick={handleOrderClick}
							order={original}
						/>
					);
				},
			},
			{
				accessorKey: 'customerInfo.customerName',
				header: '客户名称',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ row: { original } }) => {
					const { customerInfo } = original;
					return (
						<span className={`link-${PENDING_TAB_BADGE.accepting}`}>
							<PendingKeywordHighlight text={customerInfo?.customerName} />
						</span>
					);
				},
			},
			...receiptColumns,
			{
				accessorKey: 'deliveryStatus',
				header: '物流状态',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ row: { original } }) => (
					<OrderDeliveryStatusCell deliveryStatus={original.deliveryStatus} badgeVariant={PENDING_TAB_BADGE.accepting} />
				),
			},
			{
				accessorKey: 'totalPrice',
				header: '总价',
				meta: {
					headerProps: {
						className: 'text-end text-900',
					},
					cellProps: {
						className: 'text-end',
					},
				},
				cell: ({ row: { original } }) => {
					return <PendingTotalPriceHighlight totalPrice={original?.totalPrice} className='fs-11' />;
				},
			},
			buildPendingActivityAtColumn(formatDateTime),
			{
				accessorKey: 'createdAt',
				header: '创建时间',
				meta: {
					headerProps: {
						className: 'text-end text-900',
					},
					cellProps: {
						className: 'text-end',
					},
				},
				cell: ({ row: { original } }) => {
					return <span className='fs-11'>{original?.createdAt ? formatDateTime(original.createdAt) : '-'}</span>;
				},
			},
			{
				accessorKey: 'actions',
				header: '',
				enableSorting: false,
				meta: {
					cellProps: {
						className: 'text-end py-2',
					},
				},
				cell: ({ row: { original } }) => {
					const detailUrl = original?._id ? `/order/detail/${original._id}` : '#';
					return (
						<CardDropdown drop='start'>
							<div className='py-2'>
								<Dropdown.Item href={detailUrl}>查看</Dropdown.Item>
							</div>
						</CardDropdown>
					);
				},
			},
		];
	}, [acceptPendingStat, handleOrderClick, formatDateTime]);

	const displayedAcceptingOrders = useMemo(() => {
		if (!Array.isArray(acceptingOrders) || acceptingOrders.length === 0) return [];
		if (acceptPendingStat === 'total') return acceptingOrders;

		return acceptingOrders.filter((order) => {
			if (acceptPendingStat === 'accountsReceivable') {
				return Number(order?.currentAccountsReceivable) > 0;
			}
			if (acceptPendingStat === 'quasiReceivable') {
				return Number(order?.quasiReceivable) > 0;
			}
			return true;
		});
	}, [acceptingOrders, acceptPendingStat]);

	const { keyword, hasKeyword, filteredOrders } = usePendingKeywordFilter(displayedAcceptingOrders);

	const table = useAdvanceTable({
		data: filteredOrders,
		columns,
		sortable: true,
		pagination: true,
		perPage: PENDING_TABLE_PER_PAGE,
		initialState: {
			sorting: PENDING_ACTIVITY_AT_SORT,
		},
	});

	useEffect(() => {
		try {
			table.setPageIndex(0);
		} catch {
			// ignore
		}
	}, [acceptPendingStat, table]);

	if (isLoadingAcceptingOrder) {
		return <PendingListLoadingState />;
	}

	const showFooter = displayedAcceptingOrders.length > 0 || hasKeyword;
	const emptyTitle = hasKeyword
		? '无匹配订单'
		: acceptPendingStat === 'accountsReceivable'
			? '暂无有应收账款的待验收'
			: acceptPendingStat === 'quasiReceivable'
				? `暂无含${TAIL_QUASI_RECEIVABLE_LABEL}的待验收`
				: '暂无待验收';
	const emptyDescription = hasKeyword ? `当前待办中没有包含「${keyword}」的订单。` : '目前没有需要你处理的待验收。';

	return (
		<>
			<AdvanceTableProvider {...table}>
				<PendingListToolbar
					statsMobileOnly
					stats={<PendingListStatsPanel items={statsItems} onSelect={handleAcceptStatClick} showChart={false} />}
					statsOverlay={<PendingListStatsOverlay items={statsItems} onSelect={handleAcceptStatClick} />}
				>
					{filteredOrders.length === 0 ? (
						<PendingListEmptyState emptyTitle={emptyTitle} emptyDescription={emptyDescription} />
					) : (
						<AdvanceTableResponsive
							headerClassName='bg-200 text-nowrap align-middle font-sans-serif'
							rowClassName='btn-reveal-trigger text-nowrap align-middle'
							tableProps={{
								className: 'fs-10 fw-semibold mb-0 overflow-hidden',
							}}
						/>
					)}
					{showFooter ? <PendingListTableFooter showTableControls={filteredOrders.length > 0} /> : null}
				</PendingListToolbar>
			</AdvanceTableProvider>
			<AcceptModal setIsOpenPendingModal={setIsOpenPendingModal} isOpenPendingModal={isOpenPendingModal} acceptPendingStat={acceptPendingStat} />
		</>
	);
};

export default AcceptingOrderPendings;
